import { useState, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  useDraggable,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  Plus,
  Trash2,
  Square,
  Circle,
  RectangleHorizontal,
  Users,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Save,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import { sectionsApi, tablesApi } from '../services/api';

interface FloorTable {
  id: string;
  number: string;
  name: string | null;
  capacity: number;
  shape: string;
  positionX: number;
  positionY: number;
  status: string;
}

interface FloorSection {
  id: string;
  name: string;
  color: string;
  tables: FloorTable[];
}

interface FloorPlanEditorProps {
  sections: FloorSection[];
  onUpdate?: () => void;
}

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 500;
const GRID_SIZE = 20;
const TABLE_SIZE = 60;

// Draggable Table Component
function DraggableTable({
  table,
  sectionColor,
  isSelected,
  onClick,
}: {
  table: FloorTable;
  sectionColor: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: table.id,
    data: { table },
  });

  const style: React.CSSProperties = {
    position: 'absolute',
    left: table.positionX,
    top: table.positionY,
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    zIndex: isDragging ? 100 : isSelected ? 50 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const getTableStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      width: table.shape === 'rectangle' ? TABLE_SIZE * 1.5 : TABLE_SIZE,
      height: TABLE_SIZE,
      backgroundColor: isSelected ? sectionColor : `${sectionColor}20`,
      borderColor: sectionColor,
      borderWidth: 3,
      borderStyle: 'solid',
    };

    if (table.shape === 'round') {
      baseStyle.borderRadius = '50%';
    } else if (table.shape === 'rectangle') {
      baseStyle.borderRadius = 8;
    } else {
      baseStyle.borderRadius = 12;
    }

    return baseStyle;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={clsx(
        'flex flex-col items-center justify-center transition-shadow select-none',
        isDragging && 'shadow-2xl',
        isSelected && 'ring-2 ring-offset-2 ring-primary-500'
      )}
    >
      <div style={getTableStyle()} className="flex flex-col items-center justify-center">
        <span className={clsx('font-bold text-sm', isSelected ? 'text-white' : 'text-slate-700')}>
          T{table.number}
        </span>
        <span className={clsx('text-xs flex items-center gap-0.5', isSelected ? 'text-white/80' : 'text-slate-500')}>
          <Users className="w-3 h-3" />
          {table.capacity}
        </span>
      </div>
    </div>
  );
}

export default function FloorPlanEditor({ sections, onUpdate }: FloorPlanEditorProps) {
  const queryClient = useQueryClient();
  const [selectedSection, setSelectedSection] = useState<string | null>(sections[0]?.id || null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pendingChanges, setPendingChanges] = useState<Record<string, { positionX: number; positionY: number }>>({});
  const canvasRef = useRef<HTMLDivElement>(null);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 5 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 100, tolerance: 5 },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  // Save position mutation
  const savePositionMutation = useMutation({
    mutationFn: async (changes: Record<string, { positionX: number; positionY: number }>) => {
      const promises = Object.entries(changes).map(([tableId, position]) =>
        tablesApi.update(tableId, position)
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      setPendingChanges({});
      onUpdate?.();
    },
  });

  // Add table mutation
  const addTableMutation = useMutation({
    mutationFn: ({ sectionId, data }: { sectionId: string; data: { number: string; capacity: number; shape?: 'round' | 'square' | 'rectangle'; positionX?: number; positionY?: number } }) =>
      sectionsApi.addTable(sectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });

  // Delete table mutation
  const deleteTableMutation = useMutation({
    mutationFn: (id: string) => tablesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      setSelectedTable(null);
    },
  });

  // Update table mutation
  const updateTableMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      tablesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (!canvasRef.current || !selectedSection) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom - TABLE_SIZE / 2;
    const y = (e.clientY - rect.top) / zoom - TABLE_SIZE / 2;

    // Don't add table if click was on existing element
    if ((e.target as HTMLElement).closest('[data-draggable]')) return;

    const snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
    const snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;

    const section = sections.find(s => s.id === selectedSection);
    if (!section) return;

    const tableNum = section.tables.length + 1;
    addTableMutation.mutate({
      sectionId: selectedSection,
      data: {
        number: `${tableNum}`,
        capacity: 4,
        shape: 'square',
        positionX: Math.max(0, Math.min(snappedX, CANVAS_WIDTH - TABLE_SIZE)),
        positionY: Math.max(0, Math.min(snappedY, CANVAS_HEIGHT - TABLE_SIZE)),
      },
    });
  }, [selectedSection, sections, zoom, addTableMutation]);

  const handleDragStart = (event: DragStartEvent) => {
    setSelectedTable(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    const tableId = active.id as string;

    // Find which section contains this table
    for (const section of sections) {
      const table = section.tables.find(t => t.id === tableId);
      if (table) {
        const currentX = pendingChanges[tableId]?.positionX ?? table.positionX;
        const currentY = pendingChanges[tableId]?.positionY ?? table.positionY;

        const newX = Math.round((currentX + delta.x) / GRID_SIZE) * GRID_SIZE;
        const newY = Math.round((currentY + delta.y) / GRID_SIZE) * GRID_SIZE;

        const clampedX = Math.max(0, Math.min(newX, CANVAS_WIDTH - (table.shape === 'rectangle' ? TABLE_SIZE * 1.5 : TABLE_SIZE)));
        const clampedY = Math.max(0, Math.min(newY, CANVAS_HEIGHT - TABLE_SIZE));

        setPendingChanges(prev => ({
          ...prev,
          [tableId]: { positionX: clampedX, positionY: clampedY },
        }));
        break;
      }
    }
  };

  const currentSection = sections.find(s => s.id === selectedSection);
  const currentTable = (() => {
    for (const section of sections) {
      const table = section.tables.find(t => t.id === selectedTable);
      if (table) return { table, sectionId: section.id, sectionColor: section.color };
    }
    return null;
  })();

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  // Get table position (with pending changes applied)
  const getTablePosition = (table: FloorTable) => ({
    ...table,
    positionX: pendingChanges[table.id]?.positionX ?? table.positionX,
    positionY: pendingChanges[table.id]?.positionY ?? table.positionY,
  });

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setSelectedSection(section.id)}
            className={clsx(
              'px-3 py-1.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2',
              selectedSection === section.id
                ? 'text-white shadow-md'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            )}
            style={{
              backgroundColor: selectedSection === section.id ? section.color : undefined,
            }}
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: selectedSection !== section.id ? section.color : 'white' }}
            />
            {section.name}
            <span className="text-xs opacity-70">({section.tables.length})</span>
          </button>
        ))}
      </div>

      {/* Canvas Toolbar */}
      <div className="flex items-center justify-between bg-slate-100 rounded-lg px-4 py-2">
        <div className="text-sm text-slate-600">
          Click on canvas to add table • Drag tables to reposition
        </div>
        <div className="flex items-center gap-2">
          {hasPendingChanges && (
            <button
              onClick={() => savePositionMutation.mutate(pendingChanges)}
              disabled={savePositionMutation.isPending}
              className="btn btn-primary btn-sm flex items-center gap-1"
            >
              {savePositionMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Positions
            </button>
          )}
          <button
            onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
            className="p-2 rounded bg-white border border-slate-200 hover:bg-slate-50"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom(Math.min(1.5, zoom + 0.1))}
            className="p-2 rounded bg-white border border-slate-200 hover:bg-slate-50"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => setZoom(1)}
            className="p-2 rounded bg-white border border-slate-200 hover:bg-slate-50"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Floor Plan Canvas */}
      <div className="overflow-auto rounded-lg border-2 border-slate-300 bg-white" style={{ WebkitOverflowScrolling: "touch" }}>
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div
            ref={canvasRef}
            onClick={handleCanvasClick}
            className="relative cursor-crosshair"
            style={{
              width: CANVAS_WIDTH * zoom,
              height: CANVAS_HEIGHT * zoom,
              backgroundImage: `
                linear-gradient(to right, #f1f5f9 1px, transparent 1px),
                linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)
              `,
              backgroundSize: `${GRID_SIZE * zoom}px ${GRID_SIZE * zoom}px`,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
            }}
          >
            {sections.map(section =>
              section.tables.map(table => {
                const positionedTable = getTablePosition(table);
                return (
                  <DraggableTable
                    key={table.id}
                    table={positionedTable}
                    sectionColor={section.color}
                    isSelected={selectedTable === table.id}
                    onClick={() => {
                      setSelectedSection(section.id);
                      setSelectedTable(table.id);
                    }}
                  />
                );
              })
            )}

            {sections.every(s => s.tables.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center text-slate-400">
                  <Square className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="font-medium">No tables yet</p>
                  <p className="text-sm">Select a section and click on the canvas to add tables</p>
                </div>
              </div>
            )}
          </div>
        </DndContext>
      </div>

      {/* Table Editor Panel */}
      {currentTable && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900">Edit Table {currentTable.table.number}</h4>
            <button
              onClick={() => {
                if (confirm(`Delete Table ${currentTable.table.number}?`)) {
                  deleteTableMutation.mutate(currentTable.table.id);
                }
              }}
              className="px-3 py-1.5 text-sm text-danger-600 hover:bg-danger-50 rounded-lg flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Table Number</label>
              <input
                type="text"
                defaultValue={currentTable.table.number}
                onBlur={(e) => {
                  if (e.target.value !== currentTable.table.number) {
                    updateTableMutation.mutate({
                      id: currentTable.table.id,
                      data: { number: e.target.value },
                    });
                  }
                }}
                className="input w-full"
                placeholder="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Display Name</label>
              <input
                type="text"
                defaultValue={currentTable.table.name || ''}
                onBlur={(e) => {
                  updateTableMutation.mutate({
                    id: currentTable.table.id,
                    data: { name: e.target.value || null },
                  });
                }}
                className="input w-full"
                placeholder="VIP Booth"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Seats</label>
              <input
                type="number"
                min="1"
                max="50"
                defaultValue={currentTable.table.capacity}
                onBlur={(e) => {
                  const capacity = parseInt(e.target.value) || 1;
                  if (capacity !== currentTable.table.capacity) {
                    updateTableMutation.mutate({
                      id: currentTable.table.id,
                      data: { capacity },
                    });
                  }
                }}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Shape</label>
              <div className="flex gap-2">
                {[
                  { value: 'square', icon: Square, label: 'Square' },
                  { value: 'round', icon: Circle, label: 'Round' },
                  { value: 'rectangle', icon: RectangleHorizontal, label: 'Booth' },
                ].map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      updateTableMutation.mutate({
                        id: currentTable.table.id,
                        data: { shape: value },
                      });
                    }}
                    className={clsx(
                      'p-2.5 rounded-lg border-2 transition-all',
                      currentTable.table.shape === value
                        ? 'border-primary-500 bg-primary-50 text-primary-600'
                        : 'border-slate-200 text-slate-400 hover:border-slate-300'
                    )}
                    title={label}
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add */}
      {currentSection && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600">Quick add to {currentSection.name}:</span>
          {[2, 4, 6, 8].map(capacity => (
            <button
              key={capacity}
              onClick={() => {
                const section = sections.find(s => s.id === selectedSection);
                if (!section) return;

                const tableNum = section.tables.length + 1;
                const row = Math.floor(section.tables.length / 4);
                const col = section.tables.length % 4;

                addTableMutation.mutate({
                  sectionId: selectedSection!,
                  data: {
                    number: `${tableNum}`,
                    capacity,
                    shape: capacity >= 6 ? 'rectangle' : 'square',
                    positionX: col * 120 + 40,
                    positionY: row * 100 + 40,
                  },
                });
              }}
              className="px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center gap-1.5"
            >
              <Users className="w-4 h-4" />
              <span className="font-medium">{capacity}-top</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
